import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useFormik } from 'formik';
import OvTextField from '../atoms/OvTextField';
import OvCompactButton from '../atoms/OvCompactButton';
import { PLACEMENT_DESCRIPTION_MAX_LENGTH } from '../../../common/model/dto/video/video-constants';
import { placementDescriptionError } from '../../../common/utils/video-placement-utils';

interface OvVideoPlacementDescriptionFormProps {
  id: string;
  description: string;
  isSaving: boolean;
  onSave: (description: string) => void;
}

const OvVideoPlacementDescriptionForm: FC<
  OvVideoPlacementDescriptionFormProps
> = ({ id, description, isSaving, onSave }) => {
  const { t } = useTranslation();

  const formik = useFormik({
    initialValues: { description },
    enableReinitialize: true,
    onSubmit: (values) => onSave(values.description.trim()),
  });

  const error = placementDescriptionError(formik.values.description);
  const isChanged = formik.values.description.trim() !== description;

  return (
    <Form onSubmit={formik.handleSubmit} noValidate>
      <OvTextField
        id={id}
        size="small"
        label={t('videos.placements.fields.description')}
        fullWidth
        autoComplete="off"
        error={!!error}
        helperText={
          error &&
          t(`videos.placements.descriptionErrors.${error}`, {
            maxDescription: PLACEMENT_DESCRIPTION_MAX_LENGTH,
          })
        }
        {...formik.getFieldProps('description')}
      />
      {isChanged && (
        <SaveButton
          type="submit"
          tone="primary"
          isBusy={isSaving}
          disabled={!!error}
        >
          {t('common.actions.save')}
        </SaveButton>
      )}
    </Form>
  );
};

export default OvVideoPlacementDescriptionForm;

const Form = styled.form`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`;

const SaveButton = styled(OvCompactButton)`
  && {
    flex-shrink: 0;
    margin-top: 0.25rem;
  }
`;
